/**
 * Full-page property detail (matches the mockup's `body.detail-open` swap).
 *
 * Renders inside the existing Find page in place of the search row + grid —
 * Deals.tsx hides them when a property is selected and mounts this. No
 * Dialog/modal wrapper. The top action bar (Back / Save / Share / Add to
 * Pipeline) lives here; the actual data + tabs live in PropertyDetailContent.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Heart, Plus, Save, Share2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { StrategyPickerDialog } from "@/components/pipeline/StrategyPickerDialog";
import { useSavedProperties } from "@/hooks/use-saved-properties";
import { usePipeline } from "@/hooks/use-pipeline";
import { supabase } from "@/integrations/supabase/client";
import { resolveStageDisplay } from "@/lib/pipeline-display";
import { toUserMessage } from "@/lib/supabase-errors";
import { cn } from "@/lib/utils";
import type { MockProperty } from "@/data/mockProperties";
import type { Strategy } from "@/types/pipeline";

import {
  PropertyDetailContent,
  type PropertyDetailContentHandle,
  type PropertyDetailSaveState,
} from "./PropertyDetailContent";
import { toPropertySnapshot } from "./detail-helpers";

interface PropertyDetailPageProps {
  property: MockProperty;
  /** "Back to Search" / "Back to Pipeline" depending on origin. */
  fromPipeline?: boolean;
  /**
   * Opened from the Properties (owned) section. Routes "Back" to Properties and
   * hides the Checklist tab — owned properties have no actionable checklist.
   */
  fromProperties?: boolean;
  onBack: () => void;
}

export function PropertyDetailPage({
  property,
  fromPipeline,
  fromProperties,
  onBack,
}: PropertyDetailPageProps) {
  const { savedProperties, addToPipeline, toggleSaved, refetch: refetchSaved } =
    useSavedProperties();
  const { pipelines } = usePipeline();
  const [strategyPickerOpen, setStrategyPickerOpen] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  // Save-changes button state — bubbled up from PropertyDetailContent so
  // we can drive the header button from the page chrome.
  const detailRef = useRef<PropertyDetailContentHandle>(null);
  const [saveState, setSaveState] = useState<PropertyDetailSaveState>({
    isDirty: false,
    isSaving: false,
    canSave: false,
  });

  const snapshot = toPropertySnapshot(property);

  // Find existing pipeline_properties row for this property (if any).
  const savedRow = savedProperties.find((p) => p.property_id === snapshot.id);
  const pipelinePropertyId = savedRow?.id ?? null;
  const row = savedRow as unknown as
    | {
        pipeline_id?: string | null;
        strategy?: Strategy | null;
      }
    | undefined;
  const initialStrategy = (row?.strategy ?? "BRRRR") as Strategy;
  // A property is "in pipeline" only when it has a non-null pipeline_id
  // (i.e. it has been promoted to an actual pipeline stage, not just saved
  // to the wishlist). This drives the header button set.
  const inPipeline = Boolean(row?.pipeline_id);
  const isSaved = Boolean(pipelinePropertyId);
  const pipelineTitle = row?.pipeline_id
    ? resolveStageDisplay(row.pipeline_id, pipelines).label
    : null;

  // Lift strategy state so the Pipeline Strategy Bar above the hero stays in
  // sync with the in-panel Financing strategy bar. Seeds from the persisted
  // pipeline_properties row, and re-seeds whenever we navigate to a deal that
  // was promoted with a different strategy.
  const [strategy, setStrategy] = useState<Strategy>(initialStrategy);
  useEffect(() => {
    setStrategy(initialStrategy);
  }, [initialStrategy]);

  // Strategy changes are deferred until the user clicks "Save changes" — we
  // only mutate local state here. Dirty-tracking in PropertyDetailContent
  // picks up the divergence vs. `lastSavedStrategyRef` and the page-side
  // `handleStrategySave` below patches `pipeline_properties` on save.
  const handleStrategyChange = (next: Strategy) => {
    setStrategy(next);
  };

  // Save-time strategy persistence. Called from PropertyDetailContent.save()
  // only when the strategy diverges from its last-saved baseline.
  const [isStrategySaving, setIsStrategySaving] = useState(false);
  const handleStrategySave = useCallback(
    async (next: Strategy): Promise<boolean> => {
      if (!pipelinePropertyId || !row?.pipeline_id) return false;
      setIsStrategySaving(true);
      try {
        const { error } = await supabase
          .from("pipeline_properties")
          .update({ strategy: next } as never)
          .eq("id", pipelinePropertyId)
          .select("id");
        if (error) throw error;
        toast.success(`Strategy changed to ${next}`);
        await refetchSaved();
        return true;
      } catch (err: unknown) {
        toast.error(toUserMessage(err, "Failed to update strategy"));
        return false;
      } finally {
        setIsStrategySaving(false);
      }
    },
    [pipelinePropertyId, row?.pipeline_id, refetchSaved],
  );

  async function handleStrategySelected(strategy: Strategy) {
    if (!property) return;
    setStrategyPickerOpen(false);
    setIsPromoting(true);
    try {
      // Pull the current deal inputs from the detail view so they get
      // persisted into property_setting alongside the pipeline_properties
      // insert. Strategy is folded into the same write so the row lands
      // with its strategy already set (no separate promote call).
      const settings = detailRef.current?.getSavePayload();
      const savedId = await addToPipeline(property, {
        strategy,
        settings,
      });
      if (!savedId) {
        toast.error("Failed to add property to pipeline");
      }
    } finally {
      setIsPromoting(false);
    }
  }

  async function handleSaveToggle() {
    await toggleSaved(property);
  }

  function handleSaveChanges() {
    detailRef.current?.save();
  }

  const saveDisabled =
    !saveState.canSave ||
    !saveState.isDirty ||
    saveState.isSaving ||
    isStrategySaving;
  const isSaving = saveState.isSaving || isStrategySaving;

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Top action bar */}
      <div className="flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="gap-2"
          aria-label={
            fromPipeline
              ? "Back to pipeline"
              : fromProperties
                ? "Back to properties"
                : "Back to search"
          }
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          {fromPipeline
            ? "Back to Pipeline"
            : fromProperties
              ? "Back to Properties"
              : "Back to Search"}
        </Button>
        <div className="flex items-center gap-2">
          {inPipeline ? (
            <>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                disabled={saveDisabled}
                className="gap-2"
              >
                <Save className="h-4 w-4" aria-hidden />
                {isSaving
                  ? "Saving…"
                  : saveState.isDirty
                    ? "Save changes"
                    : "Saved"}
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled>
                <Share2 className="h-4 w-4" aria-hidden />
                Share
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToggle}
                className="gap-2"
              >
                <Heart
                  className={cn(
                    "h-4 w-4",
                    isSaved && "fill-primary text-primary",
                  )}
                  aria-hidden
                />
                {isSaved ? "Saved" : "Save"}
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled>
                <Share2 className="h-4 w-4" aria-hidden />
                Share
              </Button>
              <Button
                size="sm"
                onClick={() => setStrategyPickerOpen(true)}
                disabled={isPromoting}
                className="gap-2"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Add to Pipeline
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Pipeline Strategy Bar is rendered inside PropertyDetailContent's
          left column so it sits above the hero (matches the design's
          `.detail-left > .pipeline-strategy-bar`), not above the whole grid. */}

      {/* Detail content */}
      <PropertyDetailContent
        ref={detailRef}
        snapshot={snapshot}
        pipelinePropertyId={pipelinePropertyId}
        pipelineId={row?.pipeline_id ?? null}
        pipelineTitle={pipelineTitle}
        initialStrategy={initialStrategy}
        strategy={strategy}
        onStrategyChange={handleStrategyChange}
        onStrategySave={handleStrategySave}
        pipelineStrategy={row?.strategy ?? null}
        hideChecklist={fromProperties}
        fromProperties={fromProperties}
        onSaveStateChange={setSaveState}
      />

      <StrategyPickerDialog
        open={strategyPickerOpen}
        onOpenChange={setStrategyPickerOpen}
        onSelect={handleStrategySelected}
        isPending={isPromoting}
      />
    </div>
  );
}
